<template>
  <div>
    <!-- Navbar superior — oculta en páginas públicas -->
    <nav
      v-if="
        $route.meta?.requiresAuth !== false &&
        ![
          'landing',
          'vaquiano',
          'baquiano',
          'tfp',
          'tfpDev',
          'tfpAgro',
          'login',
          'register',
          'terms',
          'suscripciones',
          'forgotPassword',
          'invitacion',
          'verify',
        ].includes($route.name)
      "
      class="navbar navbar-expand-lg navbar-dark px-3"
    >
      <div class="d-flex w-100 justify-content-between align-items-center">
        <!-- Logo izquierda con color dinámico según versión de Vaquiano -->
        <div class="d-flex align-items-center">
          <img
            :src="currentBrandLogo"
            :alt="currentBrandAlt"
            height="46"
            class="me-2"
          />
        </div>

        <!-- Menú de usuario derecha -->
        <div class="d-flex align-items-center gap-2">
          <!-- Toggle Dark Mode -->
          <!-- <button
            class="theme-toggle"
            @click="toggleTheme"
            :title="isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'"
            aria-label="Cambiar tema"
          >
            <i class="bi" :class="isDark ? 'bi-sun-fill' : 'bi-moon-fill'"></i>
          </button> -->

          <!-- Avatar con dropdown (visible si hay sesión) -->
          <div
            v-if="isAuthenticated"
            class="navbar-profile dropdown position-relative"
            ref="profileDropdownContainer"
          >
            <div
              class="position-relative profile-avatar"
              id="userDropdown"
              role="button"
              tabindex="0"
              :aria-expanded="isProfileOpen"
              @click.stop="toggleProfileMenu"
            >
              <img
                v-if="fotoPerfil"
                :src="fotoPerfil"
                class="avatar-photo-nav"
                :title="userName || 'Usuario'"
                alt="Foto de perfil"
              />
              <div
                v-else
                class="avatar-initials-nav"
                :title="userName || 'Usuario'"
              >
                {{ userInitials }}
              </div>
              <span class="visually-hidden">Usuario conectado</span>
            </div>

            <div
              class="profile-dropdown-card"
              :class="{ show: isProfileOpen }"
              @click.stop
            >
              <div class="profile-card-header text-center">
                <!-- Avatar centrado con opción de foto -->
                <div class="profile-avatar-wrapper mx-auto mb-2">
                  <img
                    v-if="fotoPerfil"
                    :src="fotoPerfil"
                    class="avatar-photo-profile"
                    alt="Foto de perfil"
                  />
                  <div v-else class="avatar-initials-profile">
                    {{ userInitials }}
                  </div>

                  <!-- Botón para subir / cambiar foto -->
                  <label
                    class="avatar-upload-badge"
                    :class="{ uploading: uploadingFoto }"
                    :title="
                      uploadingFoto
                        ? 'Subiendo foto...'
                        : fotoPerfil
                        ? 'Cambiar foto de perfil'
                        : 'Agregar foto de perfil'
                    "
                  >
                    <i v-if="!uploadingFoto" class="bi bi-camera-fill"></i>
                    <span
                      v-else
                      class="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <input
                      type="file"
                      ref="fotoInput"
                      class="d-none"
                      accept="image/jpeg,image/png,image/webp"
                      :disabled="uploadingFoto"
                      @change="handleFotoUpload"
                    />
                  </label>

                  <!-- Botón eliminar foto si tiene foto -->
                  <button
                    v-if="fotoPerfil && !uploadingFoto"
                    type="button"
                    class="avatar-remove-badge"
                    title="Eliminar foto de perfil"
                    @click.stop="handleFotoRemove"
                  >
                    <i class="bi bi-x"></i>
                  </button>
                </div>

                <!-- Nombre y Correo -->
                <h6
                  class="profile-user-name mb-0 text-truncate"
                  :title="userName"
                >
                  {{ userName || "Usuario" }}
                </h6>
                <p
                  v-if="userEmail"
                  class="profile-user-email mb-2 text-truncate"
                  :title="userEmail"
                >
                  {{ userEmail }}
                </p>

                <!-- Badges de Rol y Modo centrados -->
                <div
                  class="d-flex flex-wrap align-items-center justify-content-center gap-1 mb-2"
                >
                  <span
                    v-if="currentRole"
                    class="badge rounded-pill px-2 py-1"
                    :class="getRoleBadgeClass"
                  >
                    <i :class="getRoleIcon" class="me-1"></i>
                    {{ getRoleName }}
                  </span>
                  <span
                    v-if="currentModoProduccion"
                    class="modo-badge-dropdown"
                    :class="modoClass"
                  >
                    <i class="bi" :class="modoIcon"></i>
                    {{ modoLabel }}
                  </span>
                </div>
              </div>

              <!-- Estancia info box -->
              <div class="profile-estancia-box mb-3">
                <div
                  class="d-flex align-items-center justify-content-between gap-2"
                >
                  <span class="profile-box-label">
                    <i class="bi bi-geo-alt-fill text-primary me-1"></i>
                    Estancia
                  </span>
                  <span
                    class="profile-box-value text-truncate"
                    :title="currentTamboName"
                  >
                    {{ currentTamboName || "Sin estancia" }}
                  </span>
                </div>
              </div>

              <!-- Acciones -->
              <div class="profile-card-actions d-flex flex-column gap-2">
                <button
                  type="button"
                  class="btn btn-cambiar-estancia w-100"
                  title="Cambiar Estancia"
                  @click="goToTambos"
                >
                  <i class="bi bi-arrow-repeat me-1"></i>
                  Cambiar Estancia
                </button>

                <hr class="profile-card-divider my-1" />

                <button
                  type="button"
                  class="btn btn-logout w-100"
                  @click="handleLogout"
                >
                  <i class="bi bi-box-arrow-right me-2"></i> Cerrar sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- ------ Sidebar Section ------ -->

    <!-- Overlay para cerrar sidebar al tocar fuera (solo móviles) -->
    <div
      v-if="showSidebar && isSidebarExpanded"
      class="sidebar-overlay d-md-none"
      @click="closeSidebar"
    ></div>

    <!-- Botón toggle para móviles -->
    <button
      v-if="showSidebar"
      class="sidebar-toggle-btn d-md-none"
      @click="toggleSidebar"
      :aria-expanded="isSidebarExpanded"
      aria-label="Expandir menú"
      :class="{ 'sidebar-toggle-expanded': isSidebarExpanded }"
    >
      <i
        class="bi"
        :class="isSidebarExpanded ? 'bi-chevron-left' : 'bi-chevron-right'"
      ></i>
    </button>

    <div
      v-if="showSidebar"
      class="sidebar"
      :class="{ 'sidebar-expanded': isSidebarExpanded }"
    >
      <nav class="nav flex-column justify-content-start sidebar-nav">
        <!-- ═══ INICIO ═══ -->
        <div class="nav-group">
          <router-link
            class="nav-link"
            :to="{ name: 'inicio' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-house-door-fill me-2"></i></span>
            <span class="description fw-bold">Inicio</span>
          </router-link>
        </div>

        <!-- ═══ LECHERÍA (tambo, tambo_feedlot) ═══ -->
        <div class="nav-group" v-if="hasLecheria">
          <div class="nav-group-label">
            <span class="label-text">Tambo</span>
          </div>
          <router-link
            class="nav-link"
            :to="{ name: 'vacas' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-droplet-fill me-2"></i></span>
            <span class="description">Rodeo Lechero</span>
          </router-link>
          <router-link
            v-if="showRoute('lotes')"
            class="nav-link"
            to="/lotes"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-grid-3x3-gap me-2"></i></span>
            <span class="description">Lotes de Tambo</span>
          </router-link>
          <router-link
            v-if="showRoute('compraAnimales')"
            class="nav-link"
            :to="{ name: 'compraAnimales' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-cart-plus me-2"></i></span>
            <span class="description text-nowrap">Compra de animales</span>
          </router-link>
          <router-link
            v-if="hasPermission('ventas.leer') && isOnline"
            class="nav-link"
            :to="{ name: 'ventas' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-cash-stack me-2"></i></span>
            <span class="description text-nowrap">Venta de animales</span>
          </router-link>
          <router-link
            v-if="showRoute('veterinario')"
            class="nav-link"
            to="/veterinario"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-heart-pulse me-2"></i></span>
            <span class="description">Inventario Vet.</span>
          </router-link>
        </div>

        <!-- ═══ CRÍA (solo_cria, ciclo_completo — sin lechería) ═══ -->
        <div class="nav-group" v-if="hasCria && !hasLecheria">
          <div class="nav-group-label">
            <span class="label-text">Hacienda / Cría</span>
          </div>
          <router-link
            class="nav-link"
            :to="{ name: 'cria' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-bezier2 me-2"></i></span>
            <span class="description">Rodeo de Cría</span>
          </router-link>
          <template v-if="criaEficienciaVisible">
            <router-link
              v-if="showRoute('criaEficiencia')"
              class="nav-link"
              :to="{ name: 'criaEficiencia' }"
              active-class="active"
            >
              <span class="icon"
                ><i class="bi bi-graph-up-arrow me-2"></i
              ></span>
              <span class="description text-nowrap"
                >Eficiencia reproductiva</span
              >
            </router-link>
            <router-link
              v-if="showRoute('criaRankings')"
              class="nav-link"
              :to="{ name: 'criaRankings' }"
              active-class="active"
            >
              <span class="icon"><i class="bi bi-trophy me-2"></i></span>
              <span class="description text-nowrap">Rankings de vientres</span>
            </router-link>
            <router-link
              v-if="showRoute('criaCalidad')"
              class="nav-link"
              :to="{ name: 'criaCalidad' }"
              active-class="active"
            >
              <span class="icon"
                ><i class="bi bi-clipboard2-check me-2"></i
              ></span>
              <span class="description text-nowrap">Calidad de datos</span>
            </router-link>
          </template>
          <router-link
            v-if="showRoute('lotes')"
            class="nav-link"
            to="/lotes"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-grid-3x3-gap me-2"></i></span>
            <span class="description">Lotes de Cría</span>
          </router-link>
          <router-link
            v-if="showRoute('compraAnimales')"
            class="nav-link"
            :to="{ name: 'compraAnimales' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-cart-plus me-2"></i></span>
            <span class="description text-nowrap">Compra de animales</span>
          </router-link>
          <router-link
            v-if="hasPermission('ventas.leer') && isOnline"
            class="nav-link"
            :to="{ name: 'ventas' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-cash-stack me-2"></i></span>
            <span class="description text-nowrap">Venta de animales</span>
          </router-link>
          <router-link
            v-if="showRoute('veterinario')"
            class="nav-link"
            to="/veterinario"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-heart-pulse me-2"></i></span>
            <span class="description">Inventario Vet.</span>
          </router-link>
        </div>

        <!-- ═══ FEEDLOT (invernada, tambo_feedlot, ciclo_completo) ═══ -->
        <div
          class="nav-group"
          v-if="hasFeedlot && showRoute('corralesFeedlot') && !isBetaMode"
        >
          <div class="nav-group-label">
            <span class="label-text">Feedlot</span>
          </div>
          <router-link
            v-if="showRoute('engorde')"
            class="nav-link"
            :to="{ name: 'engorde' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-clipboard-data me-2"></i></span>
            <span class="description text-nowrap">Engorde</span>
          </router-link>
          <router-link
            v-if="showRoute('corralesFeedlot')"
            class="nav-link"
            :to="{ name: 'corralesFeedlot' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-grid-3x3 me-2"></i></span>
            <span class="description">Corrales y Potreros</span>
          </router-link>
          <router-link
            v-if="showRoute('compraAnimales') && !hasLecheria && !hasCria"
            class="nav-link"
            :to="{ name: 'compraAnimales' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-cart-plus me-2"></i></span>
            <span class="description text-nowrap">Compra de animales</span>
          </router-link>
          <router-link
            v-if="
              hasPermission('ventas.leer') &&
              isOnline &&
              !hasLecheria &&
              !hasCria
            "
            class="nav-link"
            :to="{ name: 'ventas' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-cash-stack me-2"></i></span>
            <span class="description text-nowrap">Venta de animales</span>
          </router-link>
          <router-link
            v-if="showRoute('veterinario') && !hasLecheria && !hasCria"
            class="nav-link"
            to="/veterinario"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-heart-pulse me-2"></i></span>
            <span class="description">Inventario Vet.</span>
          </router-link>
        </div>

        <!-- ═══ ACTIVIDAD PECUARIA / LÁCTEA (solo Lechería) ═══ -->
        <div class="nav-group" v-if="hasLecheria">
          <div class="nav-group-label">
            <span class="label-text">Actividad Lechera</span>
          </div>
          <router-link
            v-if="showRoute('empresas')"
            class="nav-link"
            to="/empresas"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-building me-2"></i></span>
            <span class="description">Empresas</span>
          </router-link>
          <router-link
            v-if="showRoute('despachos') && hasLecheria"
            class="nav-link"
            :to="{ name: 'despachos' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-truck me-2"></i></span>
            <span class="description">Despachos</span>
          </router-link>
        </div>

        <!-- ═══ ALIMENTACIÓN (oculta en Solo Agro) ═══ -->
        <div
          class="nav-group"
          v-if="
            !isModoAgricola && (showRoute('dietas') || isOnline) && !isBetaMode
          "
        >
          <div class="nav-group-label">
            <span class="label-text">Alimentación</span>
          </div>
          <router-link
            v-if="showRoute('dietas')"
            class="nav-link"
            to="/dietas"
            active-class="active"
          >
            <span class="icon"
              ><i class="bi bi-clipboard2-heart me-2"></i
            ></span>
            <span class="description">Dietas</span>
          </router-link>
          <router-link
            v-if="showRoute('patioComidas') && isOnline"
            class="nav-link"
            :to="{ name: 'patioComidas' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-box-seam me-2"></i></span>
            <span class="description text-nowrap">Patio de Comidas</span>
          </router-link>
        </div>

        <!-- ═══ AGRICULTURA ═══ -->
        <div
          class="nav-group"
          v-if="(showRoute('siembras') || isOnline) && !isBetaMode"
        >
          <div class="nav-group-label">
            <span class="label-text">Agricultura</span>
          </div>
          <router-link
            v-if="isOnline"
            class="nav-link"
            :to="{ name: 'mapaAgronomico' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-globe-americas me-2"></i></span>
            <span class="description">Mapa Agronómico</span>
          </router-link>
          <router-link
            v-if="isOnline && tieneSistemaPasturas"
            class="nav-link"
            :to="{ name: 'pasturas' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-flower3 me-2"></i></span>
            <span class="description">Pasturas</span>
          </router-link>
          <router-link
            v-if="isOnline"
            class="nav-link"
            :to="{ name: 'forrajes' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-flower2 me-2"></i></span>
            <span class="description">Forrajes</span>
          </router-link>
          <router-link
            v-if="showRoute('siembras')"
            class="nav-link"
            :to="{ name: 'siembras' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-flower1 me-2"></i></span>
            <span class="description">Siembras</span>
          </router-link>
          <router-link
            v-if="showRoute('cosechas')"
            class="nav-link"
            :to="{ name: 'cosechas' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-flower2 me-2"></i></span>
            <span class="description">Cosechas</span>
          </router-link>
          <router-link
            v-if="showRoute('picados')"
            class="nav-link"
            :to="{ name: 'picados' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-scissors me-2"></i></span>
            <span class="description">Picados</span>
          </router-link>
          <router-link
            v-if="hasPermission('insumos.leer') && showRoute('insumos')"
            class="nav-link"
            :to="{ name: 'insumos' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-archive me-2"></i></span>
            <span class="description">Insumos</span>
          </router-link>
          <router-link
            v-if="showRoute('maquinarias') && isOnline"
            class="nav-link"
            :to="{ name: 'maquinarias' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-gear me-2"></i></span>
            <span class="description text-nowrap">Maquinaria y Cisterna</span>
          </router-link>
          <router-link
            v-if="hasVentaAgro && isOnline"
            class="nav-link"
            :to="{ name: 'ventaAgro' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-cash-coin me-2"></i></span>
            <span class="description text-nowrap">Venta Agrícola</span>
          </router-link>
        </div>

        <!-- ═══ OPERACIONES ═══ -->
        <div class="nav-group" v-if="isOnline && !isBetaMode">
          <div class="nav-group-label">
            <span class="label-text">Operaciones</span>
          </div>
          <router-link
            v-if="showRoute('ordenesCompra')"
            class="nav-link"
            :to="{ name: 'ordenesCompra' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-receipt me-2"></i></span>
            <span class="description text-nowrap">Órdenes de Compra</span>
          </router-link>
          <router-link
            v-if="showRoute('gastosTercerizados')"
            class="nav-link"
            :to="{ name: 'gastosTercerizados' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-wallet2 me-2"></i></span>
            <span class="description text-nowrap">Gastos tercerizados</span>
          </router-link>
        </div>

        <!-- ═══ FISCAL ═══ -->
        <div class="nav-group" v-if="isGestor && isOnline && !isBetaMode">
          <div class="nav-group-label">
            <span class="label-text">Fiscal</span>
          </div>
          <router-link
            v-if="isGestor"
            class="nav-link"
            :to="{ name: 'facturacion' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-receipt-cutoff me-2"></i></span>
            <span class="description">Facturación</span>
          </router-link>
          <router-link
            v-if="isGestor && currentModoProduccion !== 'solo_agro'"
            class="nav-link"
            :to="{ name: 'tramitesSenasa' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-shield-check me-2"></i></span>
            <span class="description">Trámites SENASA</span>
          </router-link>
          <router-link
            v-if="isGestor"
            class="nav-link"
            :to="{ name: 'cheques' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-bank me-2"></i></span>
            <span class="description">Cheques</span>
          </router-link>
          <router-link
            v-if="isGestor"
            class="nav-link"
            :to="{ name: 'comprobantesERP' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-receipt me-2"></i></span>
            <span class="description">Comprobantes</span>
          </router-link>
          <router-link
            v-if="showRoute('retenciones')"
            class="nav-link"
            :to="{ name: 'retenciones' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-receipt-cutoff me-2"></i></span>
            <span class="description">Retenciones</span>
          </router-link>
          <router-link
            v-if="showRoute('libroIva')"
            class="nav-link"
            :to="{ name: 'libroIva' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-journal-text me-2"></i></span>
            <span class="description">Libro IVA</span>
          </router-link>
        </div>

        <!-- ═══ GESTIÓN ═══ -->
        <div class="nav-group" v-if="showRoute('tareas') || isOnline">
          <div class="nav-group-label">
            <span class="label-text">Gestión</span>
          </div>
          <router-link
            v-if="showRoute('tareas') && !isBetaMode"
            class="nav-link"
            :to="{ name: 'tareas' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-check2-square me-2"></i></span>
            <span class="description">Tareas</span>
          </router-link>
          <router-link
            v-if="isGestor && isOnline && !isBetaMode"
            class="nav-link"
            :to="{ name: 'auditoria' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-clock-history me-2"></i></span>
            <span class="description text-nowrap">Cambios por Empleados</span>
          </router-link>
          <router-link
            v-if="isGestor && isOnline"
            class="nav-link"
            :to="{ name: 'participantes' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-people me-2"></i></span>
            <span class="description">Empleados</span>
          </router-link>
          <router-link
            v-if="isOnline"
            class="nav-link"
            :to="{ name: 'estadisticas' }"
            active-class="active"
          >
            <span class="icon"><i class="bi bi-bar-chart me-2"></i></span>
            <span class="description">Estadísticas</span>
          </router-link>
        </div>

        <!-- ═══ OFFLINE: Badge de operaciones pendientes ═══ -->
        <div class="nav-group" v-if="!isOnline && pendingCount > 0">
          <div class="nav-group-label">
            <span class="label-text">Sincronización</span>
          </div>
          <div
            class="nav-link text-warning px-3 py-2"
            style="font-size: 0.78rem"
          >
            <span class="icon"><i class="bi bi-cloud-arrow-up me-2"></i></span>
            <span class="description"
              >{{ pendingCount }} pendiente{{
                pendingCount > 1 ? "s" : ""
              }}</span
            >
          </div>
        </div>
      </nav>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { ROLES_INFO, RUTAS_PERMISOS } from "@/config/roles";
import { isRouteOffline } from "@/config/offlineConfig";

export default {
  name: "AppNavigation",
  data() {
    return {
      isSidebarExpanded: false,
      isDark: document.documentElement.getAttribute("data-bs-theme") === "dark",
      isProfileOpen: false,
      uploadingFoto: false,
    };
  },
  computed: {
    ...mapGetters("userTamboStore", [
      "isAuthenticated",
      "userName",
      "userEmail",
      "fotoPerfil",
      "hasSelectedTambo",
      "currentTamboName",
      "currentRole",
      "isGestor",
      "hasPermission",
      "hasFeedlot",
      "hasTamboMode",
      "hasLecheria",
      "hasCria",
      "isModoFeedlot",
      "isModoMixto",
      "isModoTambo",
      "isModoAgricola",
      "currentModoProduccion",
      "currentTipoSistema",
      "hasVentaAgro",
      "currentPermisos",
    ]),
    ...mapGetters("offlineStore", ["isOnline", "pendingCount"]),
    ...mapGetters("criaEficienciaStore", {
      criaEficienciaHabilitada: "seccionVisible",
    }),
    ...mapGetters("userTamboStore", ["currentTamboId"]),

    /** Eficiencia reproductiva: solo cría propia (solo_cria / ciclo_completo) y habilitada. */
    esCriaPropia() {
      return ["solo_cria", "ciclo_completo"].includes(
        this.currentModoProduccion
      );
    },
    criaEficienciaVisible() {
      return (
        this.esCriaPropia && this.isOnline && this.criaEficienciaHabilitada
      );
    },

    isBetaMode() {
      return process.env.VUE_APP_BETA_MODE === "true";
    },

    tieneSistemaPasturas() {
      if (
        !this.currentTipoSistema ||
        this.currentTipoSistema === "null" ||
        this.currentTipoSistema === "undefined"
      )
        return true;
      const ts = String(this.currentTipoSistema).toUpperCase();
      return ts === "PASTORIL" || ts === "MIXTO";
    },

    userInitials() {
      const name = this.userName || "";
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    getRoleName() {
      if (!this.currentRole) return "";
      return ROLES_INFO[this.currentRole]?.nombre || this.currentRole;
    },

    getRoleIcon() {
      if (!this.currentRole) return "bi-person";
      return ROLES_INFO[this.currentRole]?.icono || "bi-person";
    },

    getRoleBadgeClass() {
      if (!this.currentRole) return "bg-secondary";
      const color = ROLES_INFO[this.currentRole]?.color || "secondary";
      return `bg-${color}`;
    },

    modoLabel() {
      const p = this.currentModoProduccion;
      if (p === "invernada") return "Invernada";
      if (p === "tambo_feedlot") return "Tambo+Feedlot";
      if (p === "ciclo_completo") return "Ciclo Completo";
      if (p === "solo_cria") return "Solo Cría";
      if (p === "solo_agro") return "Agro";
      return "Tambo";
    },

    modoIcon() {
      const p = this.currentModoProduccion;
      if (p === "invernada") return "bi-diagram-3";
      if (p === "tambo_feedlot" || p === "ciclo_completo")
        return "bi-toggle-on";
      if (p === "solo_agro") return "bi-tree";
      return "bi-droplet";
    },

    modoClass() {
      const p = this.currentModoProduccion;
      if (p === "invernada") return "modo-feedlot";
      if (p === "tambo_feedlot" || p === "ciclo_completo") return "modo-mixto";
      if (p === "solo_agro") return "modo-agro";
      return "modo-tambo";
    },

    currentBrandLogo() {
      const modo = this.currentModoProduccion;
      try {
        if (modo === "invernada") {
          return require("@/assets/branding/versiones/logo-feedlot.png");
        }
        if (modo === "tambo_feedlot") {
          return require("@/assets/branding/versiones/logo-tambo-feedlot.png");
        }
        if (modo === "solo_cria") {
          return require("@/assets/branding/versiones/logo-cria.png");
        }
        if (modo === "ciclo_completo") {
          return require("@/assets/branding/versiones/logo-ciclo-completo.png");
        }
        if (modo === "solo_agro") {
          return require("@/assets/branding/versiones/logo-agro.png");
        }
        return require("@/assets/branding/versiones/logo-tambo.png");
      } catch (e) {
        return require("@/assets/logo.png");
      }
    },

    currentBrandAlt() {
      return `Vaquiano ${this.modoLabel}`;
    },

    /**
     * Determina si una ruta debe mostrarse en el sidebar.
     * Si estamos offline, solo se muestran las rutas habilitadas offline.
     * Si la ruta requiere permisos, verifica que el usuario los tenga.
     */
    showRoute() {
      return (routeName) => {
        // 0. Si hay configuración personalizada de vistas para el usuario, respetarla con prioridad
        const permsCustom = this.currentPermisos || {};
        if (
          permsCustom.vistas &&
          permsCustom.vistas[routeName] !== undefined &&
          !this.isGestor
        ) {
          if (permsCustom.vistas[routeName] === false) return false;
          if (permsCustom.vistas[routeName] === true) {
            if (!this.isOnline) return isRouteOffline(routeName);
            return true;
          }
        }
        // 1. Verificar permisos de rol
        const permsRequeridos = RUTAS_PERMISOS[routeName];
        if (permsRequeridos && permsRequeridos.length > 0) {
          const tienePermiso = permsRequeridos.some((p) =>
            this.hasPermission(p)
          );
          if (!tienePermiso) return false;
        }
        // 2. Verificar si la ruta está disponible offline
        if (!this.isOnline) return isRouteOffline(routeName);
        return true;
      };
    },

    showSidebar() {
      const noSidebarRoutes = [
        "landing",
        "vaquiano",
        "baquiano",
        "tfp",
        "tfpDev",
        "tfpAgro",
        "login",
        "register",
        "terms",
        "suscripciones",
        "forgotPassword",
        "invitacion",
        "verify",
        "sinAcceso",
        "tambos",
      ];
      return (
        this.isAuthenticated &&
        this.hasSelectedTambo &&
        this.$route.meta?.requiresAuth !== false &&
        this.$route.name !== "tambos" &&
        this.$route.path !== "/" &&
        this.$route.path !== "/tambos" &&
        !noSidebarRoutes.includes(this.$route.name)
      );
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    document.addEventListener("keydown", this.handleKeydown);
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    document.removeEventListener("keydown", this.handleKeydown);
  },

  methods: {
    ...mapActions("userTamboStore", ["logout", "updateProfilePhoto"]),

    verificarHabilitacionCria() {
      if (!this.isAuthenticated || !this.currentTamboId || !this.esCriaPropia)
        return;
      this.$store
        .dispatch("criaEficienciaStore/verificarHabilitacion")
        .catch(() => {});
    },

    toggleSidebar() {
      this.isSidebarExpanded = !this.isSidebarExpanded;
    },

    closeSidebar() {
      this.isSidebarExpanded = false;
    },

    toggleProfileMenu() {
      this.isProfileOpen = !this.isProfileOpen;
    },

    closeProfileMenu() {
      this.isProfileOpen = false;
    },

    closeUserDropdown() {
      this.closeProfileMenu();
    },

    handleClickOutside(event) {
      if (
        this.isProfileOpen &&
        this.$refs.profileDropdownContainer &&
        !this.$refs.profileDropdownContainer.contains(event.target)
      ) {
        this.closeProfileMenu();
      }
    },

    handleKeydown(event) {
      if (event.key === "Escape" && this.isProfileOpen) {
        this.closeProfileMenu();
      }
    },

    async handleFotoUpload(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith("image/")) {
        this.$notify?.error?.(
          "El archivo seleccionado no es una imagen válida"
        );
        return;
      }

      if (file.size > 8 * 1024 * 1024) {
        this.$notify?.error?.("La imagen es demasiado pesada (máximo 8 MB)");
        return;
      }

      this.uploadingFoto = true;
      try {
        const base64 = await this.resizeAndCompressImage(file, 360, 360, 0.85);
        await this.updateProfilePhoto(base64);
        this.$notify?.success?.("Foto de perfil actualizada correctamente");
      } catch (err) {
        console.error("Error al subir foto de perfil:", err);
        this.$notify?.error?.("No se pudo actualizar la foto de perfil");
      } finally {
        this.uploadingFoto = false;
        if (this.$refs.fotoInput) {
          this.$refs.fotoInput.value = "";
        }
      }
    },

    async handleFotoRemove() {
      if (!confirm("¿Deseas eliminar tu foto de perfil?")) return;
      this.uploadingFoto = true;
      try {
        await this.updateProfilePhoto("");
        this.$notify?.success?.("Foto de perfil eliminada");
      } catch (err) {
        console.error("Error al eliminar foto de perfil:", err);
        this.$notify?.error?.("No se pudo eliminar la foto de perfil");
      } finally {
        this.uploadingFoto = false;
      }
    },

    resizeAndCompressImage(file, maxDimension = 360, quality = 0.85) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            let width = img.width;
            let height = img.height;

            const minDim = Math.min(width, height);
            const startX = (width - minDim) / 2;
            const startY = (height - minDim) / 2;

            const targetDim = Math.min(minDim, maxDimension);

            const canvas = document.createElement("canvas");
            canvas.width = targetDim;
            canvas.height = targetDim;
            const ctx = canvas.getContext("2d");

            ctx.drawImage(
              img,
              startX,
              startY,
              minDim,
              minDim,
              0,
              0,
              targetDim,
              targetDim
            );

            resolve(canvas.toDataURL("image/jpeg", quality));
          };
          img.onerror = reject;
          img.src = e.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    goToTambos() {
      this.closeSidebar();
      this.closeProfileMenu();
      this.$router.push({ name: "tambos", query: { cambiar: "true" } });
    },

    handleLogout() {
      this.closeSidebar();
      this.closeProfileMenu();
      this.logout();
      this.$router.push({ name: "login" });
    },

    toggleTheme() {
      // Clase transitoria para animación suave
      document.documentElement.classList.add("theme-transitioning");
      this.isDark = !this.isDark;
      const theme = this.isDark ? "dark" : "light";
      document.documentElement.setAttribute("data-bs-theme", theme);
      localStorage.setItem("tambopal-theme", theme);
      // Quitar clase de transición después de la animación
      setTimeout(() => {
        document.documentElement.classList.remove("theme-transitioning");
      }, 350);
    },
  },

  watch: {
    // Cerrar sidebar y dropdown al cambiar de ruta
    $route() {
      this.closeSidebar();
      this.closeProfileMenu();
      // Si la verificación de cría falló por un error pasajero, reintentar al navegar.
      if (this.$store.getters["criaEficienciaStore/habilitacion"] === "error") {
        this.verificarHabilitacionCria();
      }
    },
    // Comprobar habilitación de eficiencia de cría al entrar a un establecimiento (§3)
    currentTamboId: {
      immediate: true,
      handler() {
        this.verificarHabilitacionCria();
      },
    },
    currentModoProduccion() {
      this.verificarHabilitacionCria();
    },
  },
};
</script>

<style scoped>
/* ------ Navbar Style Section ------ */

.navbar {
  height: calc(65px + env(safe-area-inset-top, 0px));
  padding-top: env(safe-area-inset-top, 0px);
  position: fixed;
  background-color: #123364;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1050;
}

/* Indicador de Modo de Producción en Dropdown */
.modo-badge-dropdown {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  margin-left: 4px;
}

.modo-badge-dropdown.modo-tambo {
  background-color: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border-color: rgba(59, 130, 246, 0.3);
}

.modo-badge-dropdown.modo-feedlot {
  background-color: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
  border-color: rgba(245, 158, 11, 0.3);
}

.modo-badge-dropdown.modo-mixto {
  background-color: rgba(16, 185, 129, 0.1);
  color: #10b981;
  border-color: rgba(16, 185, 129, 0.3);
}

/* ------ Navbar Style Section Ends ------ */

/* ------ SideBar Style Section ------ */

.sidebar {
  position: fixed;
  top: calc(65px + env(safe-area-inset-top, 0px));
  left: 0;
  width: var(--sidebar-width, 240px);
  height: calc(100% - 65px - env(safe-area-inset-top, 0px));
  background-color: #0f2b55;
  padding-top: 6px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  transition: width 0.3s ease;
  z-index: 1040;
}

/* --- Group labels --- */
.nav-group {
  margin-bottom: 2px;
}

.nav-group-label {
  padding: 10px 18px 3px;
  display: flex;
  align-items: center;
}

.nav-group-label .label-text {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: rgba(255, 255, 255, 0.35);
  white-space: nowrap;
}

/* Botón toggle para expandir/colapsar sidebar en móviles */
.sidebar-toggle-btn {
  position: fixed;
  top: calc(65px + env(safe-area-inset-top, 0px) + 8px);
  left: 45px; /* Posición inicial desde el borde izquierdo */
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background-color: #0a387ee5;
  border: 1px solid #fff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1041; /* Por encima del sidebar (1040), por debajo de overlays y modales */
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.4);
  transition: all 0.3s ease;
}

.sidebar-toggle-btn.sidebar-toggle-expanded {
  left: calc(
    var(--sidebar-width, 240px) - 12px
  ); /* Ajustar según el ancho expandido */
  background-color: #0a387ee5;
}

.sidebar-toggle-btn:hover,
.sidebar-toggle-btn:focus {
  background-color: #0b5ed7;
  border-color: #fff;
  outline: none;
  transform: scale(1.05);
}

.sidebar-toggle-btn:active {
  transform: scale(0.95);
  background-color: #0a58ca;
}

.sidebar-toggle-btn:focus:not(:active) {
  background-color: #0a387ee5;
  transform: none;
}

.sidebar-toggle-btn i {
  font-size: 1rem;
  font-weight: bold;
  transition: transform 0.3s ease;
}

/* Estilos del scrollbar */
.sidebar::-webkit-scrollbar {
  width: 4px;
}

.sidebar::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

.sidebar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.35);
}

.sidebar .nav-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 18px;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.88rem;
  flex-shrink: 0;
  border-left: 3px solid transparent;
  transition: all 0.15s ease;
}

.sidebar .nav-link .description {
  white-space: nowrap;
}

.sidebar .nav-link:hover {
  background-color: rgba(255, 255, 255, 0.08);
  color: #fff;
  border-left-color: rgba(255, 255, 255, 0.3);
}

.sidebar .nav-link.active {
  background-color: rgba(255, 255, 255, 0.12);
  color: #fff;
  border-left-color: #3b82f6;
  font-weight: 600;
}

.sidebar .nav-link .icon i {
  width: 20px;
  text-align: center;
}

/* ------ SideBar Style Section Ends------ */

@media (max-width: 768px) {
  .sidebar {
    width: 55px;
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  }

  /* Estado expandido en móviles */
  .sidebar.sidebar-expanded {
    width: var(--sidebar-width, 240px);
    box-shadow: 4px 0 15px rgba(0, 0, 0, 0.3);
  }

  .sidebar .description {
    display: none;
    white-space: nowrap;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .sidebar .nav-group-label {
    display: none;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  /* Mostrar descripción y labels cuando está expandido */
  .sidebar.sidebar-expanded .description,
  .sidebar.sidebar-expanded .nav-group-label {
    display: flex;
    opacity: 1;
  }

  .sidebar .nav-link {
    width: 100%;
    justify-content: center;
    padding: 8px 5px;
    border-left-width: 0;
    transition: all 0.3s ease;
  }

  /* Alinear a la izquierda cuando está expandido */
  .sidebar.sidebar-expanded .nav-link {
    justify-content: flex-start;
    padding: 7px 18px;
    border-left-width: 3px;
  }

  .sidebar .nav-link .icon {
    margin: 0;
  }

  .sidebar .nav-link .icon i {
    margin: 0 !important;
    transition: margin 0.3s ease;
  }

  /* Restaurar margen del icono cuando está expandido */
  .sidebar.sidebar-expanded .nav-link .icon i {
    margin-right: 0.5rem !important;
  }

  /* Scrollbar más delgado en móvil */
  .sidebar::-webkit-scrollbar {
    width: 3px;
  }

  /* Ajustar posición del botón cuando está expandido */
  .sidebar.sidebar-expanded .sidebar-toggle-btn {
    right: -16px;
  }

  /* Overlay para cerrar sidebar */
  .sidebar-overlay {
    position: fixed;
    top: calc(65px + env(safe-area-inset-top, 0px));
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 1040;
    animation: fadeIn 0.2s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
}

/* Estilos adicionales para el menú de usuario */
.profile-avatar {
  cursor: pointer;
}

/* Avatar con iniciales - navbar */
.avatar-initials-nav {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4a90d9, #357abd);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.5px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  user-select: none;
}
.avatar-initials-nav:hover {
  transform: scale(1.08);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

/* Avatar con foto - navbar */
.avatar-photo-nav {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.5);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  display: block;
}
.avatar-photo-nav:hover {
  transform: scale(1.08);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

/* Wrapper avatar perfil dropdown */
.profile-avatar-wrapper {
  position: relative;
  width: 76px;
  height: 76px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Avatar con iniciales - dropdown perfil */
.avatar-initials-profile {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4a90d9, #357abd);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.65rem;
  letter-spacing: 1px;
  user-select: none;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

/* Avatar con foto - dropdown perfil */
.avatar-photo-profile {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e2e8f0;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

/* Botón subir foto */
.avatar-upload-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #2563eb;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}
.avatar-upload-badge:hover {
  background: #1d4ed8;
  transform: scale(1.1);
}
.avatar-upload-badge.uploading {
  background: #64748b;
  cursor: wait;
}

/* Botón eliminar foto */
.avatar-remove-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ef4444;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  padding: 0;
  line-height: 1;
}
.avatar-remove-badge:hover {
  background: #dc2626;
  transform: scale(1.15);
}

.dropdown-item {
  cursor: pointer;
}

/* ============================================================
   DROPDOWN PERFIL USUARIO (CARD MODERNA)
   ============================================================ */
.navbar-profile {
  position: relative;
}

.profile-dropdown-card {
  position: absolute;
  top: calc(100% + 12px);
  right: 0 !important;
  left: auto !important;
  width: 290px;
  max-width: calc(100vw - 24px);
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 20px 40px -10px rgba(15, 23, 42, 0.18),
    0 4px 12px rgba(15, 23, 42, 0.08);
  padding: 1.25rem 1rem 1rem;
  z-index: 1060;
  display: none;
  animation: dropdownFade 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.profile-dropdown-card.show {
  display: block;
}

@keyframes dropdownFade {
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.profile-user-name {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}

.profile-user-email {
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 2px;
}

.profile-estancia-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 8px 12px;
}

.profile-box-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.profile-box-value {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1e293b;
  max-width: 155px;
}

.btn-cambiar-estancia {
  background: #f1f5f9;
  color: #0f274a;
  border: 1px solid #cbd5e1;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 8px 12px;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.btn-cambiar-estancia:hover {
  background: #e2e8f0;
  color: #0284c7;
  border-color: #94a3b8;
}

.profile-card-divider {
  border: none;
  border-top: 1px solid #e2e8f0;
}

.btn-logout {
  background: transparent;
  color: #dc2626;
  border: none;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 8px 12px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-logout:hover {
  background: #fef2f2;
  color: #b91c1c;
}

/* Modo oscuro */
[data-bs-theme="dark"] .profile-dropdown-card {
  background: #1e293b;
  border-color: #334155;
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.5);
}

[data-bs-theme="dark"] .profile-user-name {
  color: #f8fafc;
}

[data-bs-theme="dark"] .profile-user-email {
  color: #94a3b8;
}

[data-bs-theme="dark"] .profile-estancia-box {
  background: #0f172a;
  border-color: #334155;
}

[data-bs-theme="dark"] .profile-box-value {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .btn-cambiar-estancia {
  background: #334155;
  color: #f8fafc;
  border-color: #475569;
}

[data-bs-theme="dark"] .btn-cambiar-estancia:hover {
  background: #475569;
}

[data-bs-theme="dark"] .btn-logout:hover {
  background: rgba(220, 38, 38, 0.15);
}
</style>
